import React from 'react';

const DashboardIllustration: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <svg
      className={className}
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="tealGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0c6577" />
          <stop offset="100%" stopColor="#10839b" />
        </linearGradient>
        
        <linearGradient id="greenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#80b149" />
          <stop offset="100%" stopColor="#b6d438" />
        </linearGradient>

        <filter id="shadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.3"/>
        </filter>
      </defs>

      {/* Background gradient circles */}
      <circle cx="200" cy="150" r="100" fill="url(#tealGrad)" opacity="0.1">
        <animate attributeName="r" values="100;110;100" dur="5s" repeatCount="indefinite" />
      </circle>
      <circle cx="600" cy="450" r="120" fill="url(#greenGrad)" opacity="0.1">
        <animate attributeName="r" values="120;130;120" dur="6s" repeatCount="indefinite" />
      </circle>

      {/* Main Dashboard Container */}
      <rect x="150" y="100" width="500" height="400" rx="20" fill="white" opacity="0.95" filter="url(#shadow)"/>

      {/* Header Bar */}
      <rect x="150" y="100" width="500" height="60" rx="20" fill="url(#tealGrad)" opacity="0.9"/>
      <rect x="150" y="140" width="500" height="20" rx="0" fill="url(#tealGrad)" opacity="0.9"/>

      {/* Header Icons/Indicators */}
      <circle cx="180" cy="130" r="8" fill="white" opacity="0.8"/>
      <circle cx="205" cy="130" r="8" fill="white" opacity="0.8"/>
      <circle cx="230" cy="130" r="8" fill="white" opacity="0.8"/>

      {/* Chart 1 - Line Chart (Top Left) */}
      <g id="lineChart">
        <rect x="170" y="180" width="220" height="140" rx="10" fill="url(#tealGrad)" opacity="0.1"/>
        
        {/* Chart lines */}
        <polyline
          points="190,280 220,250 250,270 280,230 310,240 340,210 370,220"
          stroke="url(#greenGrad)"
          strokeWidth="3"
          fill="none"
          opacity="0.8"
        >
          <animate
            attributeName="points"
            values="190,280 220,250 250,270 280,230 310,240 340,210 370,220;
                    190,280 220,260 250,250 280,240 310,230 340,220 370,210;
                    190,280 220,250 250,270 280,230 310,240 340,210 370,220"
            dur="4s"
            repeatCount="indefinite"
          />
        </polyline>

        {/* Data points */}
        {[190, 220, 250, 280, 310, 340, 370].map((x, i) => (
          <circle
            key={`point-${i}`}
            cx={x}
            cy={280 - i * 10}
            r="4"
            fill="url(#tealGrad)"
            opacity="0.9"
          >
            <animate
              attributeName="r"
              values="4;6;4"
              dur="2s"
              begin={`${i * 0.2}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}

        {/* Y-axis labels */}
        <line x1="185" y1="190" x2="185" y2="290" stroke="#0c6577" strokeWidth="1" opacity="0.3"/>
        <line x1="185" y1="290" x2="375" y2="290" stroke="#0c6577" strokeWidth="1" opacity="0.3"/>
      </g>

      {/* Chart 2 - Bar Chart (Top Right) */}
      <g id="barChart">
        <rect x="410" y="180" width="220" height="140" rx="10" fill="url(#greenGrad)" opacity="0.1"/>
        
        {/* Bars */}
        {[
          { x: 430, height: 60 },
          { x: 460, height: 80 },
          { x: 490, height: 50 },
          { x: 520, height: 90 },
          { x: 550, height: 70 },
          { x: 580, height: 85 }
        ].map((bar, i) => (
          <rect
            key={`bar-${i}`}
            x={bar.x}
            y={300 - bar.height}
            width="20"
            height="0"
            rx="3"
            fill="url(#greenGrad)"
            opacity="0.8"
          >
            <animate
              attributeName="height"
              values={`0;${bar.height};${bar.height}`}
              dur="2s"
              begin={`${i * 0.1}s`}
              fill="freeze"
            />
            <animate
              attributeName="y"
              values={`300;${300 - bar.height};${300 - bar.height}`}
              dur="2s"
              begin={`${i * 0.1}s`}
              fill="freeze"
            />
          </rect>
        ))}

        {/* Axes */}
        <line x1="425" y1="190" x2="425" y2="305" stroke="#80b149" strokeWidth="1" opacity="0.3"/>
        <line x1="425" y1="305" x2="615" y2="305" stroke="#80b149" strokeWidth="1" opacity="0.3"/>
      </g>

      {/* Chart 3 - Donut Chart (Bottom Left) */}
      <g id="donutChart" transform="translate(280, 410)">
        <rect x="-110" y="-70" width="220" height="140" rx="10" fill="url(#tealGrad)" opacity="0.1"/>
        
        {/* Donut segments */}
        <circle cx="0" cy="0" r="45" stroke="url(#tealGrad)" strokeWidth="20" fill="none" opacity="0.8"
          strokeDasharray="70 212"
          transform="rotate(-90)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="-90 0 0"
            to="270 0 0"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        
        <circle cx="0" cy="0" r="45" stroke="url(#greenGrad)" strokeWidth="20" fill="none" opacity="0.8"
          strokeDasharray="100 182"
          strokeDashoffset="-70"
          transform="rotate(-90)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="-90 0 0"
            to="270 0 0"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Center circle */}
        <circle cx="0" cy="0" r="25" fill="white" opacity="0.9"/>
        
        {/* Percentage text placeholder */}
        <circle cx="0" cy="0" r="15" fill="url(#greenGrad)" opacity="0.3">
          <animate attributeName="r" values="15;18;15" dur="2s" repeatCount="indefinite" />
        </circle>
      </g>

      {/* Chart 4 - Stats Cards (Bottom Right) */}
      <g id="statsCards">
        <rect x="410" y="340" width="220" height="140" rx="10" fill="url(#greenGrad)" opacity="0.1"/>
        
        {/* Card 1 */}
        <rect x="420" y="350" width="90" height="50" rx="8" fill="url(#tealGrad)" opacity="0.2"/>
        <circle cx="435" cy="375" r="8" fill="url(#greenGrad)" opacity="0.8">
          <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" />
        </circle>
        <rect x="450" y="367" width="50" height="4" rx="2" fill="url(#tealGrad)" opacity="0.6"/>
        <rect x="450" y="377" width="35" height="4" rx="2" fill="url(#tealGrad)" opacity="0.4"/>

        {/* Card 2 */}
        <rect x="520" y="350" width="90" height="50" rx="8" fill="url(#greenGrad)" opacity="0.2"/>
        <circle cx="535" cy="375" r="8" fill="url(#tealGrad)" opacity="0.8">
          <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="0.5s" repeatCount="indefinite" />
        </circle>
        <rect x="550" y="367" width="50" height="4" rx="2" fill="url(#greenGrad)" opacity="0.6"/>
        <rect x="550" y="377" width="40" height="4" rx="2" fill="url(#greenGrad)" opacity="0.4"/>

        {/* Card 3 */}
        <rect x="420" y="410" width="90" height="50" rx="8" fill="url(#greenGrad)" opacity="0.2"/>
        <circle cx="435" cy="435" r="8" fill="#b6d438" opacity="0.8">
          <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="1s" repeatCount="indefinite" />
        </circle>
        <rect x="450" y="427" width="50" height="4" rx="2" fill="url(#greenGrad)" opacity="0.6"/>
        <rect x="450" y="437" width="45" height="4" rx="2" fill="url(#greenGrad)" opacity="0.4"/>

        {/* Card 4 */}
        <rect x="520" y="410" width="90" height="50" rx="8" fill="url(#tealGrad)" opacity="0.2"/>
        <circle cx="535" cy="435" r="8" fill="#b6d438" opacity="0.8">
          <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="1.5s" repeatCount="indefinite" />
        </circle>
        <rect x="550" y="427" width="50" height="4" rx="2" fill="url(#tealGrad)" opacity="0.6"/>
        <rect x="550" y="437" width="30" height="4" rx="2" fill="url(#tealGrad)" opacity="0.4"/>
      </g>

      {/* Floating data points around dashboard */}
      {[...Array(10)].map((_, i) => {
        const angle = (i * 36) * (Math.PI / 180);
        const distance = 280;
        const cx = 400 + distance * Math.cos(angle);
        const cy = 300 + distance * Math.sin(angle);
        
        return (
          <circle
            key={`float-${i}`}
            cx={cx}
            cy={cy}
            r="4"
            fill="url(#greenGrad)"
            opacity="0"
          >
            <animate
              attributeName="opacity"
              values="0;0.8;0"
              dur="3s"
              begin={`${i * 0.3}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="r"
              values="4;6;4"
              dur="3s"
              begin={`${i * 0.3}s`}
              repeatCount="indefinite"
            />
          </circle>
        );
      })}
    </svg>
  );
};

export default DashboardIllustration;